import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  FileText, Printer, Calendar, TrendingUp, 
  DollarSign, ShoppingCart, Package, Filter
} from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusLabels = {
  pending: 'En attente',
  confirmed: 'Confirmée',
  processing: 'En préparation',
  shipped: 'Expédiée',
  delivered: 'Livrée',
  cancelled: 'Annulée'
};

const statusColors = {
  pending: 'bg-[#0BB76E]/20 text-[#0BB76E]',
  confirmed: 'bg-[#1a1a2e]/20 text-[#1a1a2e]',
  processing: 'bg-[#0BB76E]/20 text-[#0BB76E]',
  shipped: 'bg-[#1a1a2e]/20 text-[#1a1a2e]',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-200 text-brand-dark/80'
};

export default function AdminSalesReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: ''
  });
  const printRef = useRef(null);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async (startDate = '', endDate = '') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/admin/sales-report`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchReport(filters.start_date, filters.end_date);
  };

  const handleClearFilter = () => {
    setFilters({ start_date: '', end_date: '' });
    fetchReport();
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Rapport des Ventes - ALINA ELEGANCE</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              padding: 40px; 
              color: #1a1a2e;
              line-height: 1.6;
            }
            .header { 
              text-align: center; 
              margin-bottom: 40px;
              border-bottom: 2px solid #0BB76E;
              padding-bottom: 20px;
            }
            .header h1 { 
              color: #1a1a2e; 
              font-size: 28px;
              margin-bottom: 5px;
            }
            .header p { color: #666; font-size: 14px; }
            .stats-grid { 
              display: grid; 
              grid-template-columns: repeat(4, 1fr); 
              gap: 20px; 
              margin-bottom: 40px;
            }
            .stat-card { 
              border: 1px solid #ddd; 
              padding: 20px; 
              text-align: center;
              border-radius: 8px;
            }
            .stat-card h3 { color: #666; font-size: 12px; text-transform: uppercase; }
            .stat-card p { color: #1a1a2e; font-size: 24px; font-weight: bold; margin-top: 5px; }
            .stat-card p.orange { color: #0BB76E; }
            .section { margin-bottom: 40px; }
            .section h2 { 
              color: #1a1a2e; 
              font-size: 18px; 
              margin-bottom: 15px;
              border-bottom: 1px solid #eee;
              padding-bottom: 10px;
            }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            th { background: #f5f5f5; font-weight: 600; color: #1a1a2e; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .text-orange { color: #0BB76E; font-weight: bold; }
            .footer { 
              margin-top: 40px; 
              text-align: center; 
              color: #999; 
              font-size: 12px;
              border-top: 1px solid #eee;
              padding-top: 20px;
            }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ALINA ELEGANCE</h1>
            <p>Rapport des Ventes${filters.start_date || filters.end_date ? ` - Du ${filters.start_date || 'début'} au ${filters.end_date || "aujourd'hui"}` : " - Depuis l'ouverture"}</p>
            <p>Généré le ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          </div>

          <div class="stats-grid">
            <div class="stat-card">
              <h3>Chiffre d'affaires</h3>
              <p class="orange">${data?.summary?.total_revenue?.toFixed(2) || 0} CAD</p>
            </div>
            <div class="stat-card">
              <h3>Commandes</h3>
              <p>${data?.summary?.total_orders || 0}</p>
            </div>
            <div class="stat-card">
              <h3>Panier moyen</h3>
              <p>${data?.summary?.average_order_value?.toFixed(2) || 0} CAD</p>
            </div>
            <div class="stat-card">
              <h3>En attente</h3>
              <p class="orange">${data?.summary?.pending_revenue?.toFixed(2) || 0} CAD</p>
            </div>
          </div>

          <div class="section">
            <h2>Répartition par statut</h2>
            <table>
              <tr>
                <th>Statut</th>
                <th class="text-right">Nombre</th>
              </tr>
              ${Object.entries(data?.by_status || {}).map(([status, count]) => `
                <tr>
                  <td>${statusLabels[status] || status}</td>
                  <td class="text-right">${count}</td>
                </tr>
              `).join('')}
            </table>
          </div>

          <div class="section">
            <h2>Répartition par méthode de paiement</h2>
            <table>
              <tr>
                <th>Méthode</th>
                <th class="text-right">Commandes</th>
                <th class="text-right">Chiffre d'affaires</th>
              </tr>
              ${Object.entries(data?.by_payment_method?.counts || {}).map(([method, count]) => `
                <tr>
                  <td>${method === 'stripe' || method === 'card' ? 'Carte bancaire' : method === 'interac' ? 'Interac' : method === 'cod' ? 'À la livraison' : method}</td>
                  <td class="text-right">${count}</td>
                  <td class="text-right text-orange">${(data?.by_payment_method?.revenue?.[method] || 0).toFixed(2)} CAD</td>
                </tr>
              `).join('')}
            </table>
          </div>

          ${data?.top_products?.length > 0 ? `
          <div class="section">
            <h2>Produits les plus vendus</h2>
            <table>
              <tr>
                <th>Produit</th>
                <th class="text-right">Quantité</th>
                <th class="text-right">Chiffre d'affaires</th>
              </tr>
              ${data.top_products.map(p => `
                <tr>
                  <td>${p.name}</td>
                  <td class="text-right">${p.quantity}</td>
                  <td class="text-right text-orange">${p.revenue.toFixed(2)} CAD</td>
                </tr>
              `).join('')}
            </table>
          </div>
          ` : ''}

          <div class="section">
            <h2>Liste des commandes (${data?.orders?.length || 0})</h2>
            <table>
              <tr>
                <th>N° Commande</th>
                <th>Date</th>
                <th>Client</th>
                <th class="text-center">Statut</th>
                <th class="text-center">Paiement</th>
                <th class="text-right">Montant</th>
              </tr>
              ${(data?.orders || []).map(order => `
                <tr>
                  <td>#${order.order_id.slice(-8).toUpperCase()}</td>
                  <td>${new Date(order.created_at).toLocaleDateString('fr-FR')}</td>
                  <td>${order.shipping_address?.name || '-'}</td>
                  <td class="text-center">${statusLabels[order.order_status] || order.order_status}</td>
                  <td class="text-center">${order.payment_status === 'paid' ? 'Payé' : order.payment_status === 'cod_pending' ? 'À la livraison' : 'En attente'}</td>
                  <td class="text-right text-orange">${order.total.toFixed(2)} CAD</td>
                </tr>
              `).join('')}
            </table>
          </div>

          <div class="footer">
            <p>ALINA ELEGANCE - Rapport généré automatiquement</p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-[#0BB76E] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-sales-report" ref={printRef}>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl text-[#1a1a2e] flex items-center gap-3">
              <FileText className="h-8 w-8 text-[#0BB76E]" />
              Rapport des Ventes
            </h1>
            <p className="text-brand-dark/60">
              {filters.start_date || filters.end_date 
                ? `Du ${filters.start_date || 'début'} au ${filters.end_date || "aujourd'hui"}`
                : "Depuis l'ouverture de l'entreprise"}
            </p>
          </div>
          <Button 
            onClick={handlePrint}
            className="bg-[#0BB76E] text-white hover:bg-[#1a1a2e]"
            data-testid="print-report-btn"
          >
            <Printer className="h-4 w-4 mr-2" /> Imprimer le rapport
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-brand-white border-brand-gold/20 shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="text-[#1a1a2e] font-heading flex items-center gap-2">
              <Filter className="h-5 w-5 text-[#0BB76E]" /> Filtrer par période
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label className="text-brand-dark/80">Date de début</Label>
                <Input 
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  className="mt-1 bg-brand-white border-brand-gold/20 text-[#1a1a2e] w-44"
                />
              </div>
              <div>
                <Label className="text-brand-dark/80">Date de fin</Label>
                <Input 
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  className="mt-1 bg-brand-white border-brand-gold/20 text-[#1a1a2e] w-44"
                />
              </div>
              <Button onClick={handleFilter} className="bg-[#0BB76E] text-white hover:bg-[#1a1a2e]">
                Appliquer
              </Button>
              {(filters.start_date || filters.end_date) && (
                <Button onClick={handleClearFilter} variant="ghost" className="text-brand-dark/60">
                  Réinitialiser
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-brand-white border-brand-gold/20 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-brand-dark/60">Chiffre d'affaires</CardTitle>
              <DollarSign className="h-5 w-5 text-[#0BB76E]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#0BB76E]">{data?.summary?.total_revenue?.toFixed(2) || 0} CAD</div>
              <p className="text-xs text-brand-dark/50">Commandes payées</p>
            </CardContent>
          </Card>

          <Card className="bg-brand-white border-brand-gold/20 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-brand-dark/60">Total commandes</CardTitle>
              <ShoppingCart className="h-5 w-5 text-[#0BB76E]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#1a1a2e]">{data?.summary?.total_orders || 0}</div>
            </CardContent>
          </Card>

          <Card className="bg-brand-white border-brand-gold/20 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-brand-dark/60">Panier moyen</CardTitle>
              <TrendingUp className="h-5 w-5 text-[#0BB76E]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#1a1a2e]">{data?.summary?.average_order_value?.toFixed(2) || 0} CAD</div>
            </CardContent>
          </Card>

          <Card className="bg-brand-white border-brand-gold/20 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-brand-dark/60">En attente</CardTitle>
              <Calendar className="h-5 w-5 text-[#0BB76E]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#0BB76E]">{data?.summary?.pending_revenue?.toFixed(2) || 0} CAD</div>
              <p className="text-xs text-brand-dark/50">À encaisser</p>
            </CardContent>
          </Card>
        </div>

        {/* By Status & Payment Method */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <Card className="bg-brand-white border-brand-gold/20 shadow-sm">
            <CardHeader>
              <CardTitle className="text-[#1a1a2e] font-heading">Par statut</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(data?.by_status || {}).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <Badge className={statusColors[status]}>{statusLabels[status] || status}</Badge>
                    <span className="text-[#1a1a2e] font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-brand-white border-brand-gold/20 shadow-sm">
            <CardHeader>
              <CardTitle className="text-[#1a1a2e] font-heading">Par méthode de paiement</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(data?.by_payment_method?.counts || {}).map(([method, count]) => (
                  <div key={method} className="flex items-center justify-between">
                    <span className="text-brand-dark/80">
                      {method === 'stripe' || method === 'card' ? 'Carte bancaire' : method === 'interac' ? 'Interac' : method === 'cod' ? 'À la livraison' : method}
                    </span>
                    <div className="text-right">
                      <span className="text-[#1a1a2e] font-semibold">{count} cmd</span>
                      <span className="text-[#0BB76E] ml-3 font-semibold">{(data?.by_payment_method?.revenue?.[method] || 0).toFixed(2)} CAD</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Products */}
        {data?.top_products?.length > 0 && (
          <Card className="bg-brand-white border-brand-gold/20 shadow-sm mb-8">
            <CardHeader>
              <CardTitle className="text-[#1a1a2e] font-heading flex items-center gap-2">
                <Package className="h-5 w-5 text-[#0BB76E]" /> Produits les plus vendus
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-brand-gold/20">
                    <TableHead className="text-brand-dark/60">Produit</TableHead>
                    <TableHead className="text-brand-dark/60 text-right">Quantité</TableHead>
                    <TableHead className="text-brand-dark/60 text-right">CA généré</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.top_products.map((product, idx) => (
                    <TableRow key={idx} className="border-brand-gold/10">
                      <TableCell className="text-[#1a1a2e]">{product.name}</TableCell>
                      <TableCell className="text-brand-dark/80 text-right">{product.quantity}</TableCell>
                      <TableCell className="text-[#0BB76E] text-right font-semibold">{product.revenue.toFixed(2)} CAD</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Monthly Breakdown */}
        {data?.monthly_breakdown?.length > 0 && (
          <Card className="bg-brand-white border-brand-gold/20 shadow-sm mb-8">
            <CardHeader>
              <CardTitle className="text-[#1a1a2e] font-heading">Évolution mensuelle</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-brand-gold/20">
                    <TableHead className="text-brand-dark/60">Mois</TableHead>
                    <TableHead className="text-brand-dark/60 text-right">Commandes</TableHead>
                    <TableHead className="text-brand-dark/60 text-right">Chiffre d'affaires</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.monthly_breakdown.map((month, idx) => (
                    <TableRow key={idx} className="border-brand-gold/10">
                      <TableCell className="text-[#1a1a2e]">{month.month}</TableCell>
                      <TableCell className="text-brand-dark/80 text-right">{month.orders}</TableCell>
                      <TableCell className="text-[#0BB76E] text-right font-semibold">{month.revenue.toFixed(2)} CAD</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* All Orders */}
        <Card className="bg-brand-white border-brand-gold/20 shadow-sm">
          <CardHeader>
            <CardTitle className="text-[#1a1a2e] font-heading">
              Toutes les commandes ({data?.orders?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-brand-gold/20">
                    <TableHead className="text-brand-dark/60">N° Commande</TableHead>
                    <TableHead className="text-brand-dark/60">Date</TableHead>
                    <TableHead className="text-brand-dark/60">Client</TableHead>
                    <TableHead className="text-brand-dark/60">Statut</TableHead>
                    <TableHead className="text-brand-dark/60">Paiement</TableHead>
                    <TableHead className="text-brand-dark/60 text-right">Montant</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.orders || []).map(order => (
                    <TableRow key={order.order_id} className="border-brand-gold/10">
                      <TableCell className="text-[#1a1a2e] font-mono">#{order.order_id.slice(-8).toUpperCase()}</TableCell>
                      <TableCell className="text-brand-dark/80">
                        {new Date(order.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell className="text-brand-dark/80">{order.shipping_address?.name || '-'}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[order.order_status]}>{statusLabels[order.order_status]}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-[#0BB76E]/20 text-[#0BB76E]'}>
                          {order.payment_status === 'paid' ? 'Payé' : order.payment_status === 'cod_pending' ? 'À la livraison' : 'En attente'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[#0BB76E] text-right font-semibold">{order.total.toFixed(2)} CAD</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
